import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowUp, Check, FileText, Paperclip, Send, X } from "lucide-react";
import HQLayout from "./HQLayout";
import IdaReadPanel from "./IdaReadPanel";
import IdaCreates from "./IdaCreates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_META = {
  exploring: { label: "Exploring", cls: "text-sky-300 border-sky-300/30 bg-sky-300/10" },
  producer_review: { label: "For Review", cls: "text-amber-300 border-amber-300/30 bg-amber-300/10" },
  approved: { label: "Approved", cls: "text-emerald-300 border-emerald-300/30 bg-emerald-300/10" },
};

const BRIEF_ROWS = [
  ["brief", "Brief"], ["concept_ready", "Concept ready"], ["timeline", "Timeline"],
  ["budget", "Budget"], ["location", "Location"], ["notes", "Notes"],
];

function fmt(iso) {
  return iso ? new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
}

function Card({ title, children, testId }) {
  return (
    <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-5 md:p-6" data-testid={testId}>
      <div className="text-[10.5px] uppercase tracking-[0.14em] text-white/40 mb-4">{title}</div>
      {children}
    </div>
  );
}

function IdaChat({ projectId, initial }) {
  const [messages, setMessages] = useState(initial || []);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "nearest" });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setBusy(true);
    setMessages((m) => [...m, { role: "producer", text }]);
    setInput("");
    try {
      const { data } = await axios.post(`${API}/hq/projects/${projectId}/ida`, { message: text });
      setMessages((m) => [...m, { role: "ida", text: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "ida", text: "I dropped the thread there. Ask me again." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Work it through with Ida" testId="hq-ida-chat">
      <div className="max-h-[320px] overflow-y-auto space-y-3 pr-1">
        {messages.length === 0 && (
          <p className="text-white/35 text-[13px] leading-[1.7]">
            Ask Ida anything about this project: pricing posture, risks, crew, whether to take it.
          </p>
        )}
        {messages.map((m, i) => (
          <div key={`${i}-${m.role}`} className={`text-[13.5px] leading-[1.65] ${m.role === "ida" ? "text-white/85" : "text-white/50"}`} data-testid={`hq-ida-msg-${m.role}`}>
            <span className="text-[10px] uppercase tracking-[0.1em] text-white/30 mr-2">{m.role === "ida" ? "Ida" : "You"}</span>
            {m.text}
          </div>
        ))}
        {busy && <div className="text-white/35 text-[13px] animate-pulse">Ida is thinking…</div>}
        <div ref={endRef} />
      </div>
      <div className="mt-4 relative">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Ask Ida…"
          className="w-full rounded-full bg-white/[0.06] border border-white/10 pl-4 pr-11 py-2.5 text-[13.5px] text-white placeholder-white/30 focus:outline-none focus:border-white/30"
          data-testid="hq-ida-input"
        />
        <button
          type="button"
          onClick={send}
          disabled={!input.trim() || busy}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white text-[#0c111c] flex items-center justify-center disabled:opacity-30"
          data-testid="hq-ida-send"
        >
          <ArrowUp className="w-4 h-4" />
        </button>
      </div>
    </Card>
  );
}

function ProductionPrep({ prep }) {
  if (!prep) return null;
  return (
    <Card title="Ida's Production Kit · Internal" testId="hq-production-prep">
      <div className="space-y-5 text-[13.5px] leading-[1.7] text-white/75">
        {Array.isArray(prep.deliverables) && (
          <div>
            <div className="text-white/40 text-[11px] uppercase tracking-[0.1em] mb-2">Deliverables</div>
            {prep.deliverables.map((d, i) => <p key={`${i}-${d}`}>· {d}</p>)}
          </div>
        )}
        {Array.isArray(prep.roadmap) && (
          <div>
            <div className="text-white/40 text-[11px] uppercase tracking-[0.1em] mb-2">Roadmap</div>
            {prep.roadmap.map((r, i) => (
              <p key={`${i}-${r.phase}`}><span className="text-white">{r.phase}:</span> {r.detail}</p>
            ))}
          </div>
        )}
        {prep.budget_framework && (
          <div>
            <div className="text-white/40 text-[11px] uppercase tracking-[0.1em] mb-2">Budget framework</div>
            <p>{prep.budget_framework}</p>
          </div>
        )}
        {Array.isArray(prep.checklist) && (
          <div>
            <div className="text-white/40 text-[11px] uppercase tracking-[0.1em] mb-2">Checklist</div>
            {prep.checklist.map((c, i) => <p key={`${i}-${c}`}>· {c}</p>)}
          </div>
        )}
        {prep.agreement_draft && (
          <div>
            <div className="text-white/40 text-[11px] uppercase tracking-[0.1em] mb-2">Agreement draft · for your review only</div>
            <pre className="whitespace-pre-wrap font-sans text-[12.5px] text-white/60 bg-white/[0.04] rounded-xl p-4 max-h-[280px] overflow-y-auto">{prep.agreement_draft}</pre>
          </div>
        )}
      </div>
    </Card>
  );
}

export default function HQProject() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // 'approve' | 'changes'
  const [noteText, setNoteText] = useState("");
  const [acting, setActing] = useState(false);
  const [actErr, setActErr] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/hq/projects/${id}`);
      setProject(data.project);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const approve = async () => {
    setActing(true);
    setActErr(null);
    try {
      const { data } = await axios.post(`${API}/hq/projects/${id}/approve`, { producer_note: noteText.trim() || null });
      setProject(data.project);
      setModal(null);
      setNoteText("");
      setTimeout(load, 45000); // pick up production prep + email events later
    } catch (e) {
      setActErr(e?.response?.data?.detail || "Approval failed. Try again.");
    } finally {
      setActing(false);
    }
  };

  const requestChanges = async () => {
    if (noteText.trim().length < 2) return;
    setActing(true);
    setActErr(null);
    try {
      await axios.post(`${API}/hq/projects/${id}/request-changes`, { note: noteText.trim() });
      setModal(null);
      setNoteText("");
      await load();
    } catch (e) {
      setActErr(e?.response?.data?.detail || "Could not send. Try again.");
    } finally {
      setActing(false);
    }
  };

  const stage = project?.stage || "exploring";
  const sm = STAGE_META[stage] || STAGE_META.exploring;
  const title = project?.recommendation?.project_title || project?.brief || "Project";

  return (
    <HQLayout>
      <main className="px-6 md:px-12 py-10 md:py-14 max-w-[1240px]">
        <Link to="/hq" className="inline-flex items-center gap-1.5 text-[13px] text-white/40 hover:text-white/80 transition-colors" data-testid="hq-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Enquiries
        </Link>

        {loading && <div className="mt-16 text-white/40 text-sm">Loading…</div>}

        {!loading && project && (
          <>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <h1 className="font-display font-extrabold text-[28px] md:text-[38px] tracking-[-0.03em] leading-[1.05]" data-testid="hq-project-title">
                {title}
              </h1>
              <span className={`text-[10.5px] uppercase tracking-[0.08em] border rounded-full px-2.5 py-1 ${sm.cls}`} data-testid="hq-project-stage">
                {sm.label}
              </span>
            </div>
            <div className="mt-2 text-white/40 text-[13px]">
              {(project.contact?.name || "Unknown")} · {project.contact?.email || "-"} · started {fmt(project.created_at)}
            </div>

            <div className="mt-8 grid grid-cols-1 xl:grid-cols-[1fr_400px] gap-6 items-start">
              {/* LEFT: the project */}
              <div className="space-y-6 min-w-0">
                <Card title="Live Project Brief" testId="hq-brief">
                  <div className="divide-y divide-white/[0.06]">
                    {BRIEF_ROWS.map(([k, label]) =>
                      project[k] ? (
                        <div key={k} className="py-3 grid grid-cols-[110px_1fr] gap-4">
                          <div className="text-white/35 text-[12px] uppercase tracking-[0.06em] pt-0.5">{label}</div>
                          <div className="text-white/85 text-[14px] leading-[1.65]">{String(project[k])}</div>
                        </div>
                      ) : null
                    )}
                    {(project.clarifications || []).map((c, i) => (
                      <div key={`${i}-${c.question}`} className="py-3 grid grid-cols-[110px_1fr] gap-4">
                        <div className="text-white/35 text-[12px] uppercase tracking-[0.06em] pt-0.5">Clarified</div>
                        <div className="text-white/85 text-[14px] leading-[1.65]">
                          <span className="text-white/45">{c.question}</span><br />{c.answer}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                {(project.attachments || []).length > 0 && (
                  <Card title="Files & References" testId="hq-files">
                    <div className="flex flex-wrap gap-2">
                      {project.attachments.map((a) => (
                        <span key={a.id} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.04] text-[12.5px] text-white/70">
                          <Paperclip className="w-3 h-3" /> {a.name}
                        </span>
                      ))}
                    </div>
                  </Card>
                )}

                {(project.updates || []).length > 0 && (
                  <Card title="Conversation" testId="hq-conversation">
                    <div className="space-y-4">
                      {project.updates.map((u) => (
                        <div key={u.id} className="text-[14px] leading-[1.65]">
                          <span className={`text-[10px] uppercase tracking-[0.1em] mr-2 ${u.author === "producer" ? "text-amber-300/80" : "text-white/35"}`}>
                            {u.author === "producer" ? "You" : "Client"}
                          </span>
                          <span className="text-white/80">{u.text}</span>
                          <span className="text-white/25 text-[11.5px] ml-2">{fmt(u.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

                {project.recommendation && (
                  <Card title="Creative Recommendation shown to client" testId="hq-recommendation">
                    <div className="space-y-3 text-[13.5px] leading-[1.7] text-white/70">
                      {project.recommendation.opening_thought && <p className="italic text-white/50">{project.recommendation.opening_thought}</p>}
                      {project.recommendation.creative_direction && <p>{project.recommendation.creative_direction}</p>}
                      {Array.isArray(project.recommendation.suggested_deliverables) && (
                        <p className="text-white/50">{project.recommendation.suggested_deliverables.join(" · ")}</p>
                      )}
                    </div>
                  </Card>
                )}

                {project.approved_blueprint && (
                  <Card title="Approved Creative Blueprint · V1" testId="hq-approved-blueprint">
                    <div className="space-y-3 text-[13.5px] leading-[1.7] text-white/75">
                      <p className="font-display font-bold text-white text-[17px]">{project.approved_blueprint.title}</p>
                      {project.approved_blueprint.tagline && <p className="italic text-white/50">&ldquo;{project.approved_blueprint.tagline}&rdquo;</p>}
                      {project.approved_blueprint.estimated_budget?.range && (
                        <p><span className="text-white/40">Budget:</span> {project.approved_blueprint.estimated_budget.range}</p>
                      )}
                      {project.approved_blueprint.estimated_timeline?.total && (
                        <p><span className="text-white/40">Timeline:</span> {project.approved_blueprint.estimated_timeline.total}</p>
                      )}
                    </div>
                  </Card>
                )}

                <ProductionPrep prep={project.production_prep} />

                <Card title="Activity & Email History" testId="hq-activity">
                  <div className="space-y-3">
                    {[...(project.activity || [])].reverse().map((e) => (
                      <div key={e.id} className="flex items-baseline gap-3 text-[13px] min-w-0">
                        <span className="text-white/25 text-[11.5px] w-[104px] shrink-0">{fmt(e.created_at)}</span>
                        <span className={`min-w-0 flex-1 break-words ${e.type === "email_failed" ? "text-red-300/70" : "text-white/70"}`}>
                          {e.label}
                          {e.meta?.to ? <span className="text-white/30 break-all"> · {e.meta.to}</span> : null}
                          {e.internal ? <span className="text-white/25 text-[10px] uppercase tracking-[0.08em] ml-2">internal</span> : null}
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>

              {/* RIGHT: Ida + actions */}
              <div className="space-y-6 xl:sticky xl:top-8">
                <IdaReadPanel project={project} onRead={(read) => setProject((p) => ({ ...p, ida_read: read }))} />

                <IdaCreates project={project} onProject={setProject} />

                {stage !== "approved" && (
                  <Card title="Producer Actions" testId="hq-actions">
                    <div className="flex flex-col gap-2.5">
                      <button
                        type="button"
                        onClick={() => { setModal("approve"); setNoteText(""); setActErr(null); }}
                        className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-emerald-400 text-[#0c111c] text-[14px] font-semibold hover:bg-emerald-300 transition-colors"
                        data-testid="hq-approve-btn"
                      >
                        <Check className="w-4 h-4" /> Approve & generate Blueprint
                      </button>
                      <button
                        type="button"
                        onClick={() => { setModal("changes"); setNoteText(""); setActErr(null); }}
                        className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-white/20 text-white/80 text-[14px] hover:border-white/50 transition-colors"
                        data-testid="hq-request-changes-btn"
                      >
                        <Send className="w-4 h-4" /> Send a note / request changes
                      </button>
                    </div>
                    <p className="mt-4 text-white/35 text-[12px] leading-[1.6]">
                      Approving locks Version 1, generates the premium blueprint, emails the client and asks Ida to prepare the production kit.
                    </p>
                  </Card>
                )}

                <IdaChat projectId={project.id} initial={project.producer_chat || []} />
              </div>
            </div>
          </>
        )}

        {/* Modal */}
        {modal && (
          <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center px-6" onClick={() => !acting && setModal(null)}>
            <div className="w-full max-w-[520px] rounded-2xl bg-[#131b2c] border border-white/10 p-7" onClick={(e) => e.stopPropagation()} data-testid="hq-action-modal">
              <div className="flex items-center justify-between">
                <h3 className="font-display font-bold text-[20px] tracking-[-0.01em]">
                  {modal === "approve" ? "Approve this project" : "Send a note to the client"}
                </h3>
                {!acting && (
                  <button type="button" onClick={() => setModal(null)} className="text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
                )}
              </div>
              <p className="mt-3 text-white/50 text-[13.5px] leading-[1.65]">
                {modal === "approve"
                  ? "Ida will write the Approved Creative Blueprint and deliver it to the client with your note. This locks Version 1."
                  : "Your note appears in the client\u2019s workspace as a message from their Producer, and the project returns to the exploring stage."}
              </p>
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                rows={4}
                placeholder={modal === "approve" ? "Optional personal note to the client…" : "What should the client refine or add?"}
                className="mt-5 w-full rounded-xl bg-white/[0.05] border border-white/10 px-4 py-3 text-[14px] text-white placeholder-white/30 focus:outline-none focus:border-white/30 resize-none"
                data-testid="hq-modal-note"
              />
              {actErr && <p className="mt-3 text-red-300/80 text-[13px]">{actErr}</p>}
              <div className="mt-5 flex gap-3">
                <button
                  type="button"
                  onClick={modal === "approve" ? approve : requestChanges}
                  disabled={acting || (modal === "changes" && noteText.trim().length < 2)}
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-[14px] font-semibold transition-colors disabled:opacity-40 ${
                    modal === "approve" ? "bg-emerald-400 text-[#0c111c] hover:bg-emerald-300" : "bg-white text-[#0c111c] hover:bg-white/90"
                  }`}
                  data-testid="hq-modal-confirm"
                >
                  {acting
                    ? (modal === "approve" ? "Ida is writing the blueprint…" : "Sending…")
                    : (modal === "approve" ? "Approve & deliver" : "Send note")}
                </button>
              </div>
              {modal === "approve" && acting && (
                <p className="mt-3 text-white/35 text-[12px]">This takes about half a minute. Keep the window open.</p>
              )}
            </div>
          </div>
        )}
      </main>
    </HQLayout>
  );
}
